/* =============================================================================
   SKU/EAN Predictive Search Patcher V5
   Za Shopify Impact theme 7.x sa section rendering

   STRATEGIJA (ispravka V4):
   - Predictive/suggest endpoint NE pretrazuje variants.sku i variants.barcode
     po defaultu — pretrazuje ih SAMO ako se prosledi resources[options][fields].
   - Zato na SVAKI /search/suggest zahtev dodajemo fields parametar koji
     ukljucuje default polja + variants.sku + variants.barcode.
   - Goli upit (q) ostavljamo netaknut; za parcijalni EAN dodajemo wildcard.
============================================================================= */

(() => {
  const DEBUG = false;
  const log = (...args) => DEBUG && console.log('[SKU/EAN]', ...args);

  const ENABLE_WILDCARD = true;

  // Default predictive polja + SKU + barcode
  const SEARCH_FIELDS = 'author,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor';
  const FIELDS_KEY = 'resources[options][fields]';

  log('V5 ucitana (fields strategija)');

  const normalize = (s) => (s || '').trim();

  const isEAN = (q) => /^[0-9]{8,14}$/.test(q);
  const isPartialEAN = (q) => /^[0-9]{4,7}$/.test(q);

  const isSKU = (q) => {
    if (q.length < 3) return false;
    if (q.includes(' ')) return false;
    if (!/^[A-Za-z0-9][A-Za-z0-9._\-\/]{1,63}$/.test(q)) return false;
    const hasDigit = /\d/.test(q);
    const hasSeparator = /[-._\/]/.test(q);
    const isAllUpper = /^[A-Z0-9._\-\/]+$/.test(q);
    return hasDigit || (isAllUpper && hasSeparator);
  };

  const hasShopifyPrefix = (q) => /^(sku|barcode|tag|vendor|product_type|title):/i.test(q);
  const hasWildcard = (q) => q.includes('*');

  const buildShopifyQuery = (raw) => {
    const q = normalize(raw);
    if (!q) return q;
    if (hasShopifyPrefix(q) || hasWildcard(q)) return q;
    if (isEAN(q) || isSKU(q)) return q;
    if (ENABLE_WILDCARD && isPartialEAN(q)) {
      const mapped = q + '*';
      log('Parcijalni EAN sa wildcard:', q, '->', mapped);
      return mapped;
    }
    return q;
  };

  // =========================================================================
  // URL/BODY PATCH
  // =========================================================================

  const shouldPatchUrl = (url) => {
    if (typeof url !== 'string') return false;
    return /\/search\/suggest(\.json)?(\?|$)/.test(url);
  };

  const patchUrlQParam = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      const rawQ = u.searchParams.get('q') || '';
      const mapped = buildShopifyQuery(rawQ);
      if (mapped !== rawQ) {
        u.searchParams.set('q', mapped);
      }
      // KLJUCNA ISPRAVKA V5: prosiri polja pretrage na SKU i barcode
      if (!u.searchParams.has(FIELDS_KEY)) {
        u.searchParams.set(FIELDS_KEY, SEARCH_FIELDS);
        log('Dodata fields polja:', u.toString());
      }
      return u.toString();
    } catch (e) {
      log('patchUrlQParam greska:', e);
      return url;
    }
  };

  const patchBodyQParam = (body) => {
    if (typeof body !== 'string') return body;
    try {
      const params = new URLSearchParams(body);
      const rawQ = params.get('q') || '';
      const mapped = buildShopifyQuery(rawQ);
      if (mapped !== rawQ) {
        params.set('q', mapped);
      }
      if (!params.has(FIELDS_KEY)) {
        params.set(FIELDS_KEY, SEARCH_FIELDS);
      }
      return params.toString();
    } catch {
      return body;
    }
  };

  // =========================================================================
  // PATCH FETCH
  // =========================================================================

  const patchFetch = () => {
    if (!window.fetch || window.__skuEanFetchPatched) return;
    window.__skuEanFetchPatched = true;
    log('window.fetch patchovan');

    const originalFetch = window.fetch;

    window.fetch = async function (input, init) {
      try {
        const isReq = input instanceof Request;
        const url = typeof input === 'string'
          ? input
          : isReq ? input.url : '';

        if (shouldPatchUrl(url)) {
          const newUrl = patchUrlQParam(url);
          const method =
            (init && init.method) ||
            (isReq && input.method) ||
            'GET';

          if (method.toUpperCase() !== 'GET') {
            let body = init && typeof init.body === 'string' ? init.body : null;
            if (!body && isReq) {
              try { body = await input.clone().text(); } catch (_) {}
            }
            if (body) {
              const patchedBody = patchBodyQParam(body);
              if (patchedBody !== body) {
                init = Object.assign({}, init || {}, { body: patchedBody });
              }
            }
          }
          if (newUrl !== url) {
            if (isReq) input = new Request(newUrl, input);
            else input = newUrl;
          }
        }
      } catch (e) {
        log('Fetch patch greska:', e);
      }

      return originalFetch.call(this, input, init);
    };
  };

  // =========================================================================
  // PATCH XHR
  // =========================================================================

  const patchXHR = () => {
    const XHR = window.XMLHttpRequest;
    if (!XHR || window.__skuEanXHRPatched) return;
    window.__skuEanXHRPatched = true;
    log('XMLHttpRequest patchovan');

    const originalOpen = XHR.prototype.open;
    const originalSend = XHR.prototype.send;

    XHR.prototype.open = function (method, url, ...rest) {
      try {
        if (shouldPatchUrl(url)) {
          this.__skuEanSuggest = true;
          url = patchUrlQParam(url);
        } else {
          this.__skuEanSuggest = false;
        }
      } catch (_) {}
      return originalOpen.call(this, method, url, ...rest);
    };

    XHR.prototype.send = function (body) {
      try {
        if (this.__skuEanSuggest && typeof body === 'string' && body.includes('q=')) {
          body = patchBodyQParam(body);
        }
      } catch (_) {}
      return originalSend.call(this, body);
    };
  };

  // =========================================================================
  // PATCH FORM SUBMIT
  // =========================================================================

  const bindSubmit = (form) => {
    if (form.__skuEanBound) return;
    form.__skuEanBound = true;

    form.addEventListener('submit', () => {
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const raw = normalize(input.value);
      if (!raw) return;
      const mapped = buildShopifyQuery(raw);
      if (mapped !== raw) {
        log('Form submit:', raw, '->', mapped);
        input.value = mapped;
      }
    });
  };

  const bindAllForms = () => {
    document.querySelectorAll('form[action*="/search"]').forEach(bindSubmit);
  };

  // =========================================================================
  // INIT
  // =========================================================================

  patchFetch();
  patchXHR();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindAllForms();
      const mo = new MutationObserver(bindAllForms);
      mo.observe(document.documentElement, { childList: true, subtree: true });
    });
  } else {
    bindAllForms();
    const mo = new MutationObserver(bindAllForms);
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  log('V5 inicijalizacija zavrsena');
})();
